// Pure functions for app loading and routing — extracted from AppHost.tsx.
//
// The suite and switcher model of the app switcher (`buildSuiteGroups`, `Surface`, `SuiteGroup`)
// moved to `@zion/shell-chrome/model` with the header (kernel apps, WS-16c, R-119).

import type { AppRegistryEntry } from '../api/types.js';

/**
 * Build a module cache key from appId and version.
 */
export function appCacheKey(appId: string, version: string): string {
  return `${appId}@${version}`;
}

/**
 * True when `pathname` falls under `mountPath` on a path-SEGMENT boundary.
 * `/docs` matches `/docs` and `/docs/intro`, but NOT `/docs-archive` — a raw
 * `startsWith` would wrongly match the latter. A root mount (`/`) matches
 * everything (its segment prefix is `/`).
 *
 * This is the ONE path-prefix predicate in the shell. Prefix matching (not
 * exact equality) is load-bearing in both places it is used: `/admin/users/123`
 * must still register as under `/admin`, and an app mounted at `/build` must
 * own `/build/repos/123` for deep links and in-app navigation to resolve. The
 * only rule is that the prefix has to terminate on a separator, which is what
 * a bare `startsWith` gets wrong — it matched `/administration` against
 * `/admin` and served a working page on an undefined route (#2139).
 *
 * ⚠️ `stripMountPath()` in `@zion/sdk-react` shares this semantic and is a
 * TRANSFORM rather than a predicate: it returns the pathname with the prefix
 * removed, for a hosted app feeding its own router. It is a separate
 * implementation because the dependency direction forbids that package
 * importing from the shell. The two must agree on case-folding and on the
 * segment boundary; if they ever disagree, this file wins, because it is the
 * side that resolves the route. A change to either belongs in both.
 */
export function isUnderMount(pathname: string, mountPath: string): boolean {
  // An empty mountPath is a DATA ERROR, not a root mount. Left untreated it
  // degrades to the prefix `/`, which matches every path — reinstating exactly
  // the swallow-everything behaviour #2139 was about, from a malformed registry
  // entry rather than from a fallback. Refuse to match instead.
  if (mountPath === '') return false;
  // Case-fold both sides to AGREE WITH THE ROUTER. react-router matches
  // case-insensitively by default, so `/ADMIN/users` resolves to the real admin
  // route while a case-sensitive predicate reported "not under /admin" — the
  // inverse of #2139: a genuine admin page wearing the wrong chrome. Verified
  // against react-router's own `matchRoutes`: `/ADMIN/users` and `/Admin/users`
  // both match `/admin > users`. A predicate that disagrees with the router is
  // the divergence that turns into an authz hole the day someone reuses it to
  // gate rather than to label.
  const p = pathname.toLowerCase();
  let mount = mountPath.toLowerCase();
  // Normalise a TRAILING SLASH on the mount, DEFENSIVELY rather than because
  // the grammar allows one. The live contract forbids it: MOUNT_PATH_GRAMMAR_
  // PATTERN is `^/[a-z][a-z0-9-]*(/[a-z0-9-]+)*$`, at
  // `contracts/src/mount-path.ts:126`, and MOUNT_PATH_MESSAGE says "with no
  // trailing slash" (`:187-190`).
  // The historical `^/[a-z][a-z0-9/-]*$` did allow `/build/`, and that comment
  // quoted it here long after it was tightened. The shell re-validates nothing
  // on read (`api/client.ts:460-464` checks only `typeof === 'string'`), so a
  // mount that predates the tightening, or one written by a producer that
  // skipped it, would leave `/build` unmatched while `/build/repos/1` matched:
  // a quiet failure, because the switcher tile routes to `mountPath` verbatim
  // and only hand-typed and in-app links would break. Guarded on length so a
  // genuine root mount (`/`) is left alone and keeps matching everything.
  if (mount.length > 1 && mount.endsWith('/')) mount = mount.slice(0, -1);
  if (p === mount) return true;
  const prefix = mount.endsWith('/') ? mount : `${mount}/`;
  return p.startsWith(prefix);
}

/**
 * The path→app match: the registry entry with the LONGEST `mountPath` that
 * contains `pathname` on a segment boundary, or `null` when none match.
 *
 * Longest-prefix (not first-match) matters in a multi-app workspace where one
 * app mounts at a prefix of another (e.g. `/brewblast` and `/brewblast/show`).
 * Naive first-match returned whichever appeared first in the registry, so the
 * deeper app resolved to the shorter-prefix app and the AppHost switch logic
 * dereferenced a lifecycle that was never registered. Matching is segment-aware
 * so `/docs` never swallows `/docs-archive`.
 *
 * This is the SOLE matcher: AppHost resolves the bundle to mount with it and
 * the Header resolves the switcher label with it, so the chrome can never name
 * a different app than the one loaded. It used to be the fallback-less core of
 * a `resolveActiveApp` wrapper that fell back to `registry[0]` when nothing
 * matched, which meant any undefined path silently mounted the first registered
 * app and served a working page (#2139). There is no fallback now — `null` is
 * the honest answer, and AppHost renders its not-found surface on it.
 */
export function matchAppByPath(
  registry: readonly AppRegistryEntry[],
  pathname: string,
): AppRegistryEntry | null {
  let best: AppRegistryEntry | null = null;
  for (const app of registry) {
    const mp = app.manifest.mountPath;
    if (
      isUnderMount(pathname, mp) &&
      (best === null || mp.length > best.manifest.mountPath.length)
    ) {
      best = app;
    }
  }
  return best;
}

/**
 * Whether the current path resolves to no surface at all, i.e. the not-found
 * page should render.
 *
 * ⛔ The SIGNATURE is the point. This deliberately cannot see `activeApp` or
 * `loadingBundle`, because a predicate built from those two is wrong in a way no
 * test can catch: they are lagging React state, so on a fresh AppHost mount with
 * an already-loaded registry the first render has a matched entry but no loaded
 * app and no raised loading flag — and a state-derived predicate is TRUE there,
 * committing the not-found page in the middle of a legitimate app launch. RTL
 * flushes effects inside `act()`, so the suite stays green either way.
 *
 * Passing only what is knowable synchronously makes that mistake impossible to
 * express rather than merely detectable. `hasError` is the one lagging input and
 * is safe: it can only make the result FALSE, so it cannot cause that flash —
 * and an error must outrank the not-found claim, because a failed registry fetch
 * may mean we are holding a stale app list and cannot honestly say a path does
 * not exist.
 *
 * ⛔ THE ROW COUNT IS NOT AN INPUT (A-des-C7-2). An `appCount > 0` conjunct used to guard a
 * genuinely empty organization's surface, and it made the answer depend on how many apps the
 * principal can see rather than on whether this address resolves. The case that exposed it: a
 * data plane whose only installed app is the admin app, viewed by a member, whose registry is
 * therefore empty — an `/admin` bookmark resolved to nothing and got a bare "No apps available"
 * sentence instead of the not-found page. One unresolved path, one answer, whatever the count.
 *
 * `loading` is not an input either, because the caller settles it first: `AppHost` returns its
 * spinner before it reaches this predicate, so a `!loading` conjunct here would be a branch no
 * call site can take.
 */
export function isSurfaceUnresolved(args: {
  readonly hasMatch: boolean;
  readonly hasError: boolean;
}): boolean {
  return !args.hasMatch && !args.hasError;
}

/**
 * Build the app summary list from registry entries.
 */
export function buildAppList(registry: readonly AppRegistryEntry[]): Array<{
  fixtureId: string;
  version: string;
  title: string;
  icon: string;
  mountPath: string;
}> {
  return registry.map(e => ({
    fixtureId: e.appId,
    version: e.version,
    title: e.manifest.title,
    icon: e.manifest.icon,
    mountPath: e.manifest.mountPath,
  }));
}
